export type TSegmentDestination = {
  name: string;
  creationName: string;
  description: string;
  website: string;
  category: string;
};

/**
 * Registry of Segment destinations. This is used to map Segment write keys to enabled destinations.
 * Data is sourced from `https://cdn.segment.com/v1/projects/SEGMENT_WRITE_KEY/integrations`.
 * As of 2025-11-06, Segment support has indicated they do not officially support this
 * endpoint, so we are statically cloning it into this file as a workaround.
 *
 * This data should be updated when a Segment destination is added, removed, enabled,
 * or disabled, or data will not flow to that destination correctly. Integrations
 * endpoint can take a few hours to update to the latest integration info.
 *
 * Note that Segment will gracefully handle a destination
 * present in code but no longer in the Segment UI.
 *
 * Last updated 2026-06-10
 */
const SegmentDestinationRegistry: Record<string, Array<TSegmentDestination>> = {
  // Production
  // @see https://cdn.segment.com/v1/projects/KDfmZqXBVlaN7GyAoJCkNdiRnTWIRYPW/integrations
  KDfmZqXBVlaN7GyAoJCkNdiRnTWIRYPW: [
    {
      name: 'Google Universal Analytics',
      creationName: 'Google Analytics',
      description:
        'Google Universal Analytics is the most popular analytics tool for the web. It’s free and provides a wide range of features. It’s especially good at measuring traffic sources and ad campaigns.',
      website: 'http://google.com/analytics',
      category: 'Analytics',
    },
    {
      name: 'Madkudu',
      creationName: 'Madkudu',
      description:
        'Intelligent lead scoring and sales acceleration for B2B SaaS.',
      website: 'http://www.madkudu.com',
      category: 'Analytics',
    },
    {
      name: 'Bing Ads',
      creationName: 'Bing Ads',
      description: 'Create advertise and campaigns your product in Bing search',
      website: 'https://advertise.bingads.microsoft.com/en-us/home',
      category: 'Advertising',
    },
    {
      name: 'LinkedIn Insight Tag',
      creationName: 'LinkedIn Insight Tag',
      description:
        'The LinkedIn Insight Tag is a piece of lightweight JavaScript code that you can add to your website to enable in-depth campaign reporting and unlock valuable insights about your website visitors. As a LinkedIn Marketing Solutions customer, you can use the LinkedIn Insight Tag to track conversions, retarget website visitors, and unlock additional insights about members interacting with your ads.',
      website: 'https://www.linkedin.com',
      category: 'Advertising',
    },
    {
      name: 'Google Ads (Gtag)',
      creationName: 'Google AdWords New',
      description:
        "Advertise on Google and put your message in front of potential customers right when they're searching for what you have to offer.",
      website: 'https://adwords.google.com',
      category: 'Advertising',
    },
    {
      name: 'Podsights',
      creationName: 'Podsights',
      description:
        'Podsights measures the effectiveness of podcast advertising. ',
      website: 'https://podsights.com',
      category: 'Advertising',
    },
    {
      name: 'Twitter Ads',
      creationName: 'Twitter Ads',
      description:
        'Advertise on Twitter and reach out to more people with promoted tweets and accounts.',
      website: 'https://ads.twitter.com/',
      category: 'Advertising',
    },
    {
      name: 'Facebook Pixel',
      creationName: 'Facebook Pixel',
      description:
        'The Facebook Pixel integration lets you measure and optimize the performance of your Facebook Ads.',
      website: 'https://developers.facebook.com/docs/facebook-pixel',
      category: 'Advertising',
    },
    {
      name: 'Google Tag Manager',
      creationName: 'Google Tag Manager',
      description:
        'Google Tag Manager lets you add or update your website tags, easily and for free.',
      website: 'https://www.google.com/analytics/tag-manager/',
      category: 'Tag Managers',
    },
    {
      name: 'Hotjar',
      creationName: 'Hotjar',
      description:
        "Hotjar is a heatmap and recording tool for tracking what your users are doing on your website, and where they're doing it.",
      website: 'http://www.hotjar.com',
      category: 'Heatmaps & Recordings',
    },
    {
      name: 'Google Analytics 4 Cloud',
      creationName: 'Actions Google Analytic 4',
      description:
        'Google Analytics 4 (GA4) is Google’s new Analytics property, which you can use for both websites and apps. Google Analytics 4 has machine learning at its core to automatically surface helpful insights and give you a complete understanding of your customers across devices and platforms. When you have Segment installed, you can make efficient use of your existing tracking implementation by using Segment to fulfill your data collection needs across all your tools that integrate with Segment, including Google Analytics 4.',
      website: 'https://support.google.com/analytics/answer/10089681',
      category: 'Analytics',
    },
  ],
  // Development
  // @see https://cdn.segment.com/v1/projects/ugcN3B35LVWDltYCzbcWVO5XWQvA0lVM/integrations
  ugcN3B35LVWDltYCzbcWVO5XWQvA0lVM: [
    {
      name: 'Google Universal Analytics',
      creationName: 'Google Analytics',
      description:
        'Google Universal Analytics is the most popular analytics tool for the web. It’s free and provides a wide range of features. It’s especially good at measuring traffic sources and ad campaigns.',
      website: 'http://google.com/analytics',
      category: 'Analytics',
    },
    {
      name: 'Google Ads (Gtag)',
      creationName: 'Google AdWords New',
      description:
        "Advertise on Google and put your message in front of potential customers right when they're searching for what you have to offer.",
      website: 'https://adwords.google.com',
      category: 'Advertising',
    },
    {
      name: 'LinkedIn Insight Tag',
      creationName: 'LinkedIn Insight Tag',
      description:
        'The LinkedIn Insight Tag is a piece of lightweight JavaScript code that you can add to your website to enable in-depth campaign reporting and unlock valuable insights about your website visitors. As a LinkedIn Marketing Solutions customer, you can use the LinkedIn Insight Tag to track conversions, retarget website visitors, and unlock additional insights about members interacting with your ads.',
      website: 'https://www.linkedin.com',
      category: 'Advertising',
    },
    {
      name: 'Twitter Ads',
      creationName: 'Twitter Ads',
      description:
        'Advertise on Twitter and reach out to more people with promoted tweets and accounts.',
      website: 'https://ads.twitter.com/',
      category: 'Advertising',
    },
    {
      name: 'Facebook Pixel',
      creationName: 'Facebook Pixel',
      description:
        'The Facebook Pixel integration lets you measure and optimize the performance of your Facebook Ads.',
      website: 'https://developers.facebook.com/docs/facebook-pixel',
      category: 'Advertising',
    },
    {
      name: 'Hotjar',
      creationName: 'Hotjar',
      description:
        "Hotjar is a heatmap and recording tool for tracking what your users are doing on your website, and where they're doing it.",
      website: 'http://www.hotjar.com',
      category: 'Heatmaps & Recordings',
    },
    {
      name: 'Google Tag Manager',
      creationName: 'Google Tag Manager',
      description:
        'Google Tag Manager lets you add or update your website tags, easily and for free.',
      website: 'https://www.google.com/analytics/tag-manager/',
      category: 'Tag Managers',
    },
  ],
};

const segmentDestinations =
  // Break convention and reference `process.env` directly instead of `import { SEGMENT_WRITE_KEY } from 'src/env';` to enable
  // dropping of unreferenced `SegmentDestinationRegistry` entries. With a module import, code gets compiled down to a
  // minified form of `SegmentDestinationRegistry[env.SEGMENT_WRITE_KEY]` and requires all registered values to
  // be retained. When using `process.env`, our env variable gets inlined as a value and unneeded registry
  // values are dropped. This ensures we are not embedding other environment (e.g. development
  // when building production) keys and values in our final bundle.
  //
  // You may verify this by running `make export` and validating this portion of the app.js bundle.
  SegmentDestinationRegistry[process.env.SEGMENT_WRITE_KEY];

if (!segmentDestinations) {
  // eslint-disable-next-line no-console
  console.warn('No destinations registered for Segment write key');
}

export default segmentDestinations ?? [];
